import { message as antdMessage } from 'antd';
import keyBy from 'lodash/keyBy';
import { actionChannel, all, call, fork, put, race, select, take, takeEvery, takeLatest, takeLeading } from 'redux-saga/effects';

import { ipCameraClient } from 'clients';
import { unsetMyPreference } from 'store/modules/myPreferences/actions';
import {
  AllowedPlateNumberResponse,
  FetchAllowedPlateNumbersResponse,
  TriggerIPCameraResponse
} from 'types/dto/ip-camera.dto';
import IPCamera, {
  IPCameraCapabilitiesResponse,
  IPCameraEvent,
  IPCameraEventPayload,
  IPCameraMetadata,
  IPCameraPhoto,
  IPCameraPreview,
  IPCameraSettings,
  LandWatchIPCameraEvent,
  PTZPreset
} from 'types/models/ip-camera';
import { EntityKind } from 'types/models/user-preference';
import { APIResponseParsed, PaginatedResponse } from 'types/response';
import { normalisePlateNumberSearch } from 'utils/integration/normalise-plate-number';

import {
  activateIPCameraPTZPresetFailure,
  activateIPCameraPTZPresetRequest,
  activateIPCameraPTZPresetSuccess,
  addAllowedPlateNumber,
  addAllowedPlateNumberFailure,
  addAllowedPlateNumberSuccess,
  createIPCamera,
  createIPCameraFailure,
  createIPCameraPTZPresetFailure,
  createIPCameraPTZPresetRequest,
  createIPCameraPTZPresetSuccess,
  createIPCameraSuccess,
  deleteAllowedPlateNumber,
  deleteAllowedPlateNumberFailure,
  deleteAllowedPlateNumberSuccess,
  deleteIPCameraPTZPresetFailure,
  deleteIPCameraPTZPresetRequest,
  deleteIPCameraPTZPresetSuccess,
  editIPCamera,
  editIPCameraFailure,
  editIPCameraSettings,
  editIPCameraSettingsFailure,
  editIPCameraSettingsSuccess,
  editIPCameraSuccess,
  fetchAllowedPlateNumbers,
  fetchAllowedPlateNumbersFailure,
  fetchAllowedPlateNumbersSuccess,
  fetchCameraById,
  fetchCameraByIdFailure,
  fetchCameraByIdSuccess,
  fetchIPCameraCapabilities,
  fetchIPCameraCapabilitiesFailure,
  fetchIPCameraCapabilitiesSuccess,
  fetchIPCameraEvents,
  fetchIPCameraEventsFailure,
  fetchIPCameraEventsSuccess,
  fetchIPCameraPTZPresetsFailure,
  fetchIPCameraPTZPresetsRequest,
  fetchIPCameraPTZPresetsSuccess,
  fetchIPCameraPhotos,
  fetchIPCameraPhotosFailure,
  fetchIPCameraPhotosSuccess,
  fetchIPCameraSettings,
  fetchIPCameraSettingsFailure,
  fetchIPCameraSettingsSuccess,
  fetchIPCameraState,
  fetchIPCameraStateFailure,
  fetchIPCameraStateSuccess,
  fetchIPCameras,
  fetchIPCamerasFailure,
  fetchIPCamerasSuccess,
  fetchMoreAllowedPlateNumbers,
  prependAllowedPlateNumbers,
  removeAllowedPlateNumbersFromList,
  removeFromIPCameraList,
  removeIPCamera,
  removeIPCameraPTZPreset,
  removeIPCameraSuccess,
  resetIPCameraUnseenPhoto,
  sendCameraPTZControlRequest,
  setAllowedPlateNumbers,
  setIPCameraCapabilities,
  setIPCameraEvents,
  setIPCameraPTZPreset,
  setIPCameraPTZPresets,
  setIPCameraPhotos,
  setIPCameraPreview,
  setIPCameraPreviewError,
  setIPCameraSettings,
  setIPCameraState,
  setIPCameraUnseenPhoto,
  snoozeIPCamera,
  snoozeIPCameraFailure,
  snoozeIPCameraSuccess,
  takeIPCameraPhoto,
  takeIPCameraPhotoFailure,
  takeIPCameraPhotoSuccess,
  takeIPCameraPreview,
  takeIPCameraPreviewFailure,
  takeIPCameraPreviewSuccess,
  triggerIPCamera,
  triggerIPCameraFailure,
  triggerIPCameraSuccess,
  updateIPCameraPTZPresetFailure,
  updateIPCameraPTZPresetRequest,
  updateIPCameraPTZPresetSuccess,
  wakeUpIPCamera,
  wakeUpIPCameraFailure,
  wakeUpIPCameraSuccess
} from './actions';
import ActionTypes from './constants';
import { mergeOverlappedEventArray } from './helpers/merge-overlapped-event-array';
import { getIPCameraEventSource, withIPCameraEventSource } from './helpers/with-ip-camera-event-source';
import { selectIPCamera } from './selector';
import { IPCamerasState } from './types';

const TRIGGER_ALREADY_IN_PROGRESS_MESSAGE = 'Please wait a few minutes before requesting again';

// ==============================
// SAGAS
// ==============================
function* requestIPCameras(action: ReturnType<typeof fetchIPCameras>) {
  const { payload: { enterpriseId, siteId } } = action;
  const response: APIResponseParsed<IPCamera[]> = yield call(ipCameraClient.fetchIPCameras, { enterpriseId, siteId });
  if (response.data) {
    // TODO: create proper mapping object; introduce DTOs for FE
    const ipCameras: IPCamerasState = keyBy(response.data, 'id');
    yield put(fetchIPCamerasSuccess(ipCameras));
  } else {
    yield put(fetchIPCamerasFailure('Failed to retrieve Security Cameras'));
  }
}

function* requestIPCameraById(action: ReturnType<typeof fetchCameraById>) {
  const { payload: { id } } = action;
  const response: APIResponseParsed<IPCamera> = yield call(ipCameraClient.fetchIPCameraById, id);
  const metadataResponse: APIResponseParsed<IPCameraMetadata> = yield call(
    ipCameraClient.fetchIPCameraONVIFStateById,
    id
  );

  if (response.data && metadataResponse.data) {
    const { status } = metadataResponse.data;
    const metadata = { status };
    const ipCamera: IPCamera = {
      ...response.data,
      metadata
    };
    yield put(fetchCameraByIdSuccess(ipCamera));
    yield put(fetchIPCameraCapabilities(id));
    if (getIPCameraEventSource(ipCamera) === 'barnOwl') {
      yield put(fetchIPCameraSettings(id));
    }
  } else {
    const message = response.error?.message || metadataResponse.error?.message || 'Failed to retrieve Security Camera';
    yield put(fetchCameraByIdFailure(message));
    antdMessage.error('Failed to retrieve Security Camera');
  }
}

function* requestTakeIPCameraPreview(action: ReturnType<typeof takeIPCameraPreview>) {
  const { payload: { ipCameraId } } = action;
  const response: APIResponseParsed<IPCameraPreview> = yield call(ipCameraClient.takeIPCameraPreview, ipCameraId);

  if (response.data) {
    yield all([
      put(takeIPCameraPreviewSuccess()),
      put(setIPCameraPreview(ipCameraId, response.data))
    ]);
  } else {
    const message = response.error.message || 'Failed to take a preview';
    yield all([
      put(takeIPCameraPreviewFailure(message)),
      put(setIPCameraPreviewError(ipCameraId, message))
    ]);
  }
}

function* requestFetchIPCameraState(action: ReturnType<typeof fetchIPCameraState>) {
  const { payload: { ipCameraId } } = action;
  const existing: IPCamera | undefined = yield select(selectIPCamera(ipCameraId));
  const response: APIResponseParsed<IPCameraMetadata> = yield call(
    ipCameraClient.fetchIPCameraONVIFStateById,
    ipCameraId
  );

  if (response.data) {
    const metadata: IPCameraMetadata = {
      status: response.data.status
    };
    yield all([
      put(fetchIPCameraStateSuccess()),
      put(setIPCameraState(ipCameraId, metadata))
    ]);
  } else {
    const message = response.error.message || 'Failed to retrieve Security Camera state';
    yield put(fetchIPCameraStateFailure(message));
    const metadata: IPCameraMetadata = {
      status: { online: false },
      error: message
    };
    yield put(setIPCameraState(ipCameraId, metadata));
  }
}

function mergedIPCameraMetadata(existing: IPCamera | undefined, returned: IPCameraMetadata): IPCameraMetadata {
  return {
    ...existing?.metadata,
    ...returned,
    status: {
      ...existing?.metadata?.status,
      ...returned.status
    }
  };
}

export function* requestSnoozeIPCamera(action: ReturnType<typeof snoozeIPCamera>) {
  const { payload: { ipCameraId, snoozeHours } } = action;
  const response: APIResponseParsed<IPCameraMetadata> = yield call(
    ipCameraClient.snoozeIPCamera,
    ipCameraId,
    snoozeHours
  );
  if (response.data) {
    const existing: IPCamera | undefined = yield select(selectIPCamera(ipCameraId));
    yield all([
      put(snoozeIPCameraSuccess()),
      put(setIPCameraState(ipCameraId, mergedIPCameraMetadata(existing, response.data)))
    ]);
    antdMessage.success('Security Camera alerts snoozed');
  } else {
    const message = response.error.message || 'Failed to snooze Security Camera alerts';
    yield put(snoozeIPCameraFailure(message));
    antdMessage.error('Failed to snooze Security Camera alerts');
  }
}

export function* requestWakeUpIPCamera(action: ReturnType<typeof wakeUpIPCamera>) {
  const { payload: { ipCameraId } } = action;
  const response: APIResponseParsed<IPCameraMetadata> = yield call(
    ipCameraClient.wakeUpIPCamera,
    ipCameraId
  );
  if (response.data) {
    const existing: IPCamera | undefined = yield select(selectIPCamera(ipCameraId));
    yield all([
      put(wakeUpIPCameraSuccess()),
      put(setIPCameraState(ipCameraId, mergedIPCameraMetadata(existing, response.data)))
    ]);
    antdMessage.success('Security Camera snooze ended');
  } else {
    const message = response.error.message || 'Failed to end Security Camera snooze';
    yield put(wakeUpIPCameraFailure(message));
    antdMessage.error('Failed to end Security Camera snooze');
  }
}

function* requestFetchIPCameraCapabilities(action: ReturnType<typeof fetchIPCameraCapabilities>) {
  const { payload: { ipCameraId, capabilityFields } } = action;
  const response: APIResponseParsed<IPCameraCapabilitiesResponse> = yield call(
    ipCameraClient.fetchIPCameraCapabilities,
    ipCameraId,
    capabilityFields
  );

  if (response.data) {
    const { capabilities, channels } = response.data;
    yield all([
      put(fetchIPCameraCapabilitiesSuccess()),
      put(setIPCameraCapabilities(ipCameraId, capabilities, channels))
    ]);
  } else {
    const message = response.error.message || 'Failed to retrieve Security Camera capabilities';
    yield put(fetchIPCameraCapabilitiesFailure(message));
  }
}

function* requestFetchIPCameraPhotos(action: ReturnType<typeof fetchIPCameraPhotos>) {
  const { payload: { ipCameraId, params } } = action;
  const response: APIResponseParsed<PaginatedResponse<IPCameraPhoto>> = yield call(
    ipCameraClient.fetchIPCameraPhotos,
    ipCameraId,
    params
  );

  if (response.data) {
    const { count, data: photos } = response.data;
    yield all([
      put(fetchIPCameraPhotosSuccess()),
      put(setIPCameraPhotos(ipCameraId, { currentPage: params.page, currentData: photos, totalDataItems: count })),
      put(resetIPCameraUnseenPhoto(ipCameraId))
    ]);
  } else {
    const message = response.error.message || 'Sorry, something went wrong.';
    yield put(fetchIPCameraPhotosFailure(message));
  }
}

export function* requestTakeIPCameraPhoto(action: ReturnType<typeof takeIPCameraPhoto>) {
  const { payload: { ipCameraId, channelId } } = action;
  const response: APIResponseParsed<IPCameraPhoto> = yield call(
    ipCameraClient.takeIPCameraSnapshot,
    ipCameraId,
    channelId
  );
  if (response.data) {
    yield all([
      put(takeIPCameraPhotoSuccess()),
      put(setIPCameraUnseenPhoto(ipCameraId, response.data))
    ]);
    antdMessage.success('Photo taken');
  } else {
    const message = response.error.message || 'Failed to take Security Camera photo';
    yield put(takeIPCameraPhotoFailure(message));
    antdMessage.error('Failed to take photo');
  }
}

export function* requestTriggerIPCamera(action: ReturnType<typeof triggerIPCamera>) {
  const { payload: { ipCameraId, triggerType } } = action;
  const response: APIResponseParsed<TriggerIPCameraResponse> = yield call(
    ipCameraClient.triggerIPCamera,
    ipCameraId,
    { triggerType }
  );
  const successMessage = triggerType === 'video'
    ? 'Recording requested. This may take a few minutes, then refresh the page to view the recording.'
    : 'Snapshot requested. The image will appear in the gallery shortly';
  const errorMessage = triggerType === 'video' ? 'Failed to request recording' : 'Failed to take photo';
  const errorFallback = triggerType === 'video'
    ? 'Failed to request Security Camera recording'
    : 'Failed to take Security Camera photo';
  if (response.data) {
    yield put(triggerIPCameraSuccess());
    if (response.data.triggered) {
      antdMessage.success(successMessage);
    } else {
      antdMessage.warning(TRIGGER_ALREADY_IN_PROGRESS_MESSAGE);
    }
  } else {
    const message = response.error.message || errorFallback;
    yield put(triggerIPCameraFailure(message));
    antdMessage.error(errorMessage);
  }
}

export function* requestFetchIPCameraSettings(
  action: ReturnType<typeof fetchIPCameraSettings>
) {
  const { payload: { ipCameraId } } = action;
  const response: APIResponseParsed<IPCameraSettings> = yield call(
    ipCameraClient.fetchIPCameraSettings,
    ipCameraId
  );
  if (response.data) {
    yield all([
      put(fetchIPCameraSettingsSuccess()),
      put(setIPCameraSettings(ipCameraId, response.data))
    ]);
  } else {
    const message = response.error.message || 'Failed to retrieve Security Camera settings';
    yield put(fetchIPCameraSettingsFailure(ipCameraId, message));
  }
}

export function* requestEditIPCameraSettings(
  action: ReturnType<typeof editIPCameraSettings>
) {
  const { payload: { ipCameraId, values } } = action;
  const response: APIResponseParsed<IPCameraSettings> = yield call(
    ipCameraClient.editIPCameraSettings,
    ipCameraId,
    values
  );
  if (response.data) {
    yield all([
      put(editIPCameraSettingsSuccess()),
      put(setIPCameraSettings(ipCameraId, response.data))
    ]);
    antdMessage.success('Security Camera settings updated');
  } else {
    const message = response.error.message || 'Failed to update Security Camera settings';
    yield put(editIPCameraSettingsFailure(message));
    antdMessage.error('Failed to update Security Camera settings');
  }
}

function* requestIPCameraPTZControl(action: ReturnType<typeof sendCameraPTZControlRequest>) {
  const { payload: { id, control } } = action;
  const response: APIResponseParsed<'OK'> = yield call(ipCameraClient.sendIPCameraControl, id, control);

  if (response.data) {
    // Do nothing
  } else {
    antdMessage.error('Unable to control Security Camera PTZ');
  }
}

function* requestIPCameraEvents(action: ReturnType<typeof fetchIPCameraEvents>) {
  const {
    payload: {
      ipCameraId,
      startDate,
      endDate,
      isAppend,
      channelId,
      disableIpCameraEventOverlapMerge,
      paging
    }
  } = action;
  const response: APIResponseParsed<IPCameraEventPayload[]> = yield call(
    ipCameraClient.fetchIPCameraEvents,
    ipCameraId,
    startDate,
    endDate,
    channelId,
    paging
  );

  if (response.data) {
    const ipCamera: IPCamera | undefined = yield select(selectIPCamera(ipCameraId));
    const source = getIPCameraEventSource(ipCamera);
    const data: IPCameraEvent[] = response.data.map((event) => withIPCameraEventSource(event, source));

    if (data.length > 0 && !isAppend) {
      yield put(setIPCameraEvents(ipCameraId, data));
    } else if (data.length > 0 && isAppend) {
      // This is explicitly a load more request
      const existingEvents: IPCameraEvent[] = (yield select(selectIPCamera(ipCameraId))).eventList || [];
      let mergeResult: IPCameraEvent[] = data;

      if (disableIpCameraEventOverlapMerge) {
        // Play-by-filename: keep every record row (filePath always present); do not merge overlaps.
        mergeResult = [...existingEvents, ...data];
      } else {
        mergeResult = mergeOverlappedEventArray(
          existingEvents.filter((event): event is LandWatchIPCameraEvent => event.source === 'landwatch'),
          data.filter((event): event is LandWatchIPCameraEvent => event.source === 'landwatch')
        );

        // If there is only one event in the response, and it is the same as the last event in the existing list,
        // then it can be implied that the search contains an ONGOING recording
        if (data.length === 1 && mergeResult.length === existingEvents.length) {
          antdMessage.info('No new recordings');
        }
      }

      yield put(setIPCameraEvents(ipCameraId, mergeResult));
    } else if (data.length === 0) {
      if (isAppend) {
        antdMessage.info('No more recordings in this date range');
      } else {
        antdMessage.info('No recordings found');

        // This helps initialise the eventList in DetailsPageContext, to distinguish between
        // no requests made vs. no events found
        yield put(setIPCameraEvents(ipCameraId, []));
      }
    }

    yield put(fetchIPCameraEventsSuccess());
  } else {
    const message = response.error.message || 'Failed to retrieve Security Camera recordings';
    yield put(fetchIPCameraEventsFailure(message));
    antdMessage.error('Failed to retrieve recordings');
  }
}

function* requestEditIPCamera(action: ReturnType<typeof editIPCamera>) {
  const { payload: { id, values } } = action;
  const response: APIResponseParsed<IPCamera> = yield call(ipCameraClient.editIPCamera, id, values);
  if (response.data) {
    yield all([
      put(editIPCameraSuccess()),
      // Repurpose the following action to save the updated Security Camera to the store
      put(fetchCameraByIdSuccess(response.data))
    ]);
    antdMessage.success('Security Camera settings updated');
  } else {
    const message = response.error.message || 'Failed to update Security Camera';
    yield put(editIPCameraFailure(message));
    antdMessage.error('Failed to update Security Camera');
  }
}

function* requestRemoveIPCamera(action: ReturnType<typeof removeIPCamera>) {
  const { payload: { id, backToIPCameraListPage } } = action;
  const response: APIResponseParsed<'OK'> = yield call(ipCameraClient.deleteIPCamera, id);
  if (response.data) {
    yield all([
      put(removeIPCameraSuccess()),
      put(removeFromIPCameraList(id)),
      put(unsetMyPreference(EntityKind.IP_CAMERA, id.toString()))
    ]);
    antdMessage.success('Security Camera removed');
    backToIPCameraListPage();
  } else {
    const message = response.error.message || 'Failed to remove Security Camera';
    yield put(editIPCameraFailure(message));
    antdMessage.error('Failed to remove Security Camera');
  }
}

function* requestFetchIPCameraPTZPresets(action: ReturnType<typeof fetchIPCameraPTZPresetsRequest>) {
  const { payload: { ipCameraId, channelId } } = action;

  const response: APIResponseParsed<PTZPreset[]> = yield call(
    ipCameraClient.fetchIPCameraPTZPresets,
    ipCameraId,
    channelId
  );
  if (response.data) {
    yield all([
      put(fetchIPCameraPTZPresetsSuccess()),
      put(setIPCameraPTZPresets(ipCameraId, response.data))
    ]);
  } else {
    const message = response.error.message || 'Failed to retrieve Security Camera PTZ Presets';
    yield put(fetchIPCameraPTZPresetsFailure(message));
    antdMessage.error('Failed to get presets');
  }
}

function* requestCreateIPCameraPTZPreset(action: ReturnType<typeof createIPCameraPTZPresetRequest>) {
  const { payload: { ipCameraId, channelId } } = action;
  const response: APIResponseParsed<PTZPreset> = yield call(
    ipCameraClient.createIPCameraPTZPreset,
    ipCameraId,
    channelId
  );

  if (response.data) {
    yield all([
      put(createIPCameraPTZPresetSuccess()),
      put(setIPCameraPTZPreset(ipCameraId, response.data))
    ]);
    antdMessage.success('Preset created');
  } else {
    const message = response.error.message || 'Failed to create Security Camera PTZ Preset';
    yield put(createIPCameraPTZPresetFailure(message));
    antdMessage.error('Failed to create preset');
  }
}

function* requestUpdateIPCameraPTZPreset(action: ReturnType<typeof updateIPCameraPTZPresetRequest>) {
  const { payload: { ipCameraId, presetId, values, channelId } } = action;
  const response: APIResponseParsed<PTZPreset> = yield call(
    ipCameraClient.updateIPCameraPTZPreset,
    ipCameraId,
    presetId,
    values,
    channelId
  );
  if (response.data) {
    yield all([
      put(updateIPCameraPTZPresetSuccess()),
      put(setIPCameraPTZPreset(ipCameraId, response.data))
    ]);
    antdMessage.success('Preset updated');
  } else {
    const message = response.error.message || 'Failed to update Security Camera PTZ Preset';
    yield put(updateIPCameraPTZPresetFailure(message));
    antdMessage.error('Failed to update preset');
  }
}

function* requestDeleteIPCameraPTZPreset(action: ReturnType<typeof deleteIPCameraPTZPresetRequest>) {
  const { payload: { ipCameraId, presetId, channelId } } = action;
  const response: APIResponseParsed<'OK'> = yield call(
    ipCameraClient.deleteIPCameraPTZPreset,
    ipCameraId,
    presetId,
    channelId
  );
  if (response.data) {
    yield all([
      put(deleteIPCameraPTZPresetSuccess()),
      put(removeIPCameraPTZPreset(ipCameraId, presetId))
    ]);
    antdMessage.success('Preset deleted');
  } else {
    const message = response.error.message || 'Failed to delete Security Camera PTZ Preset';
    yield put(deleteIPCameraPTZPresetFailure(message));
    antdMessage.error('Failed to delete preset');
  }
}

function* requestActivatePTZRequest(action: ReturnType<typeof activateIPCameraPTZPresetRequest>) {
  const { payload: { ipCameraId, presetId, channelId } } = action;
  const response: APIResponseParsed<'OK'> = yield call(
    ipCameraClient.activateIPCameraPTZPreset,
    ipCameraId,
    presetId,
    channelId
  );
  if (response.data) {
    yield put(activateIPCameraPTZPresetSuccess());
    antdMessage.success('Preset activated');
  } else {
    const message = response.error.message || 'Failed to activate Security Camera PTZ Preset';
    yield put(activateIPCameraPTZPresetFailure(message));
    antdMessage.error('Failed to activate preset');
  }
}

function* requestCreateIPCamera(action: ReturnType<typeof createIPCamera>) {
  const { payload: { newIPCamera, onSuccess } } = action;
  const response: APIResponseParsed<IPCamera> = yield call(ipCameraClient.createIPCamera, newIPCamera);
  if (response.data) {
    yield put(createIPCameraSuccess());
    antdMessage.success('Security Camera created successfully');
    if (onSuccess) {
      onSuccess();
    }
  } else {
    const message = response.error.message || 'Failed to create Security Camera';
    yield put(createIPCameraFailure(message));
    antdMessage.error('Failed to create Security Camera');
  }
}

export function* requestFetchAllowedPlateNumbers(
  action: ReturnType<typeof fetchAllowedPlateNumbers | typeof fetchMoreAllowedPlateNumbers>
) {
  const { payload: { ipCameraId, params, isAppend } } = action;
  const normalisedSearch = normalisePlateNumberSearch(params.search);
  const requestParams = { ...params, search: normalisedSearch };
  const response: APIResponseParsed<FetchAllowedPlateNumbersResponse> = yield call(
    ipCameraClient.fetchAllowedPlateNumbers,
    ipCameraId,
    requestParams
  );

  if (!response.data) {
    const message = response.error.message || 'Failed to retrieve trusted vehicles';
    yield put(fetchAllowedPlateNumbersFailure(message));
  } else {
    yield all([
      put(fetchAllowedPlateNumbersSuccess()),
      put(setAllowedPlateNumbers({
        ipCameraId,
        plateNumbers: response.data.data,
        total: response.data.count,
        page: params.page,
        totalPages: response.data.totalPages,
        isAppend,
        search: params.search ?? ''
      }))
    ]);
  }
}

export function* requestAddAllowedPlateNumber(action: ReturnType<typeof addAllowedPlateNumber>) {
  const { payload: { ipCameraId, plateNumber } } = action;
  const response: APIResponseParsed<AllowedPlateNumberResponse> = yield call(
    ipCameraClient.addAllowedPlateNumber,
    ipCameraId,
    { plateNumber }
  );

  if (!response.data) {
    const message = response.error.message || 'Failed to add licence plate';
    yield put(addAllowedPlateNumberFailure(message));
    antdMessage.error('Failed to add licence plate');
  } else {
    const { result } = response.data;
    const search: string | undefined = (yield select(selectIPCamera(ipCameraId)))?.allowedPlateNumbers?.search;

    if (search) {
      // A search is active; the new plate may or may not match it (and the filtered total
      // is server-derived), so refetch page 1 with the search rather than guess locally.
      yield all([
        put(addAllowedPlateNumberSuccess()),
        put(fetchAllowedPlateNumbers(ipCameraId, {
          page: 1,
          search
        }))
      ]);
    } else if (result > 0) {
      // Optimistically show the new plate on top, preserving loaded pages and scroll position.
      yield all([
        put(addAllowedPlateNumberSuccess()),
        put(prependAllowedPlateNumbers(ipCameraId, [plateNumber], result))
      ]);
    } else {
      // result === 0: nothing was actually added (e.g. duplicate); leave the list untouched.
      yield put(addAllowedPlateNumberSuccess());
    }
    if (result > 0) {
      antdMessage.success('Licence plate added');
    }
  }
}

export function* requestDeleteAllowedPlateNumber(action: ReturnType<typeof deleteAllowedPlateNumber>) {
  const { payload: { ipCameraId, plateNumber, onSuccess } } = action;
  const response: APIResponseParsed<AllowedPlateNumberResponse> = yield call(
    ipCameraClient.deleteAllowedPlateNumber,
    ipCameraId,
    { plateNumber }
  );

  if (!response.data) {
    const message = response.error.message || 'Failed to delete licence plate';
    yield put(deleteAllowedPlateNumberFailure(message));
    antdMessage.error('Failed to delete licence plate');
  } else {
    const { result } = response.data;

    if (result > 0) {
      // The server deleted the plate; remove it locally instead of refetching page 1,
      // which would collapse any extra pages the user loaded via scrolling and reset their position.
      yield all([
        put(deleteAllowedPlateNumberSuccess()),
        put(removeAllowedPlateNumbersFromList(ipCameraId, [plateNumber]))
      ]);
      onSuccess?.();
      antdMessage.success('Licence plate deleted');
    } else {
      // result === 0: nothing was actually deleted (e.g. plate not on list); leave the list untouched.
      yield put(deleteAllowedPlateNumberSuccess());
    }
  }
}

// ==============================
// REGISTRATION
// ==============================
function* watchIPCamerasListRequest() {
  yield takeLatest(ActionTypes.FETCH_IP_CAMERAS_REQUEST, requestIPCameras);
}

function* watchIPCameraByIdRequest() {
  yield takeLatest(ActionTypes.FETCH_CAMERA_BY_ID_REQUEST, requestIPCameraById);
}

function* watchTakeIPCameraPreviewRequest() {
  yield takeEvery(ActionTypes.TAKE_IP_CAMERA_PREVIEW_REQUEST, requestTakeIPCameraPreview);
}

function* watchFetchIPCameraStateRequest() {
  yield takeEvery(ActionTypes.FETCH_IP_CAMERA_STATE_REQUEST, requestFetchIPCameraState);
}

function* watchSnoozeIPCameraRequest() {
  yield takeLeading(ActionTypes.SNOOZE_IP_CAMERA_REQUEST, requestSnoozeIPCamera);
}

function* watchWakeUpIPCameraRequest() {
  yield takeLeading(ActionTypes.WAKE_UP_IP_CAMERA_REQUEST, requestWakeUpIPCamera);
}

function* watchFetchIPCameraCapabilitiesRequest() {
  yield takeLatest(ActionTypes.FETCH_IP_CAMERA_CAPABILITIES_REQUEST, requestFetchIPCameraCapabilities);
}

function* watchFetchIPCameraPhotosRequest() {
  yield takeLatest(ActionTypes.FETCH_IP_CAMERA_PHOTOS_REQUEST, requestFetchIPCameraPhotos);
}

function* watchTakeIPCameraPhotoRequest() {
  yield takeLatest(ActionTypes.TAKE_IP_CAMERA_PHOTO_REQUEST, requestTakeIPCameraPhoto);
}

function* warnOnTriggerIPCameraRequestWhileBusy() {
  while (true) {
    yield take(ActionTypes.TRIGGER_IP_CAMERA_REQUEST);
    antdMessage.warning(TRIGGER_ALREADY_IN_PROGRESS_MESSAGE);
  }
}

// Photo and video share this action. A second click while the first is in flight is dropped,
// and the operator is told to wait instead of the click disappearing.
export function* watchTriggerIPCameraRequest() {
  while (true) {
    const action: ReturnType<typeof triggerIPCamera> = yield take(ActionTypes.TRIGGER_IP_CAMERA_REQUEST);
    yield race({
      done: call(requestTriggerIPCamera, action),
      ignored: call(warnOnTriggerIPCameraRequestWhileBusy)
    });
  }
}

function* watchFetchIPCameraSettingsRequest() {
  yield takeLatest(ActionTypes.FETCH_IP_CAMERA_SETTINGS_REQUEST, requestFetchIPCameraSettings);
}

// One settings write at a time. takeLatest would drop the in-flight saga while its PATCH
// still finishes, so an older response can land on the camera after a newer click.
export function* watchEditIPCameraSettingsRequest() {
  const channel = yield actionChannel(ActionTypes.EDIT_IP_CAMERA_SETTINGS_REQUEST);
  while (true) {
    const action: ReturnType<typeof editIPCameraSettings> = yield take(channel);
    yield call(requestEditIPCameraSettings, action);
  }
}

function* watchIPCameraPTZControlRequest() {
  yield takeLeading(ActionTypes.SEND_CAMERA_PTZ_CONTROL_REQUEST, requestIPCameraPTZControl);
}

function* watchFetchIPCameraEventsRequest() {
  yield takeLatest(ActionTypes.FETCH_IP_CAMERA_EVENTS_REQUEST, requestIPCameraEvents);
}

function* watchEditIPCameraRequest() {
  yield takeLatest(ActionTypes.EDIT_IP_CAMERA_REQUEST, requestEditIPCamera);
}

function* watchRemoveIPCameraRequest() {
  yield takeLatest(ActionTypes.REMOVE_IP_CAMERA_REQUEST, requestRemoveIPCamera);
}

function* watchFetchIPCameraPTZPresetsRequest() {
  yield takeLatest(ActionTypes.FETCH_IP_CAMERA_PTZ_PRESETS_REQUEST, requestFetchIPCameraPTZPresets);
}

function* watchCreateIPCameraPTZPresetRequest() {
  yield takeLatest(ActionTypes.CREATE_IP_CAMERA_PTZ_PRESET_REQUEST, requestCreateIPCameraPTZPreset);
}

function* watchUpdateIPCameraPTZPresetRequest() {
  yield takeLatest(ActionTypes.UPDATE_IP_CAMERA_PTZ_PRESET_REQUEST, requestUpdateIPCameraPTZPreset);
}

function* watchDeleteIPCameraPTZPresetRequest() {
  yield takeLatest(ActionTypes.DELETE_IP_CAMERA_PTZ_PRESET_REQUEST, requestDeleteIPCameraPTZPreset);
}

function* watchActivateIPCameraPTZPresetRequest() {
  yield takeLatest(ActionTypes.ACTIVATE_IP_CAMERA_PTZ_PRESET_REQUEST, requestActivatePTZRequest);
}

function* watchCreateIPCameraRequest() {
  yield takeLatest(ActionTypes.CREATE_IP_CAMERA_REQUEST, requestCreateIPCamera);
}

function* watchFetchAllowedPlateNumbersRequest() {
  yield takeLatest(ActionTypes.FETCH_ALLOWED_PLATE_NUMBERS_REQUEST, requestFetchAllowedPlateNumbers);
}

function* watchAddAllowedPlateNumberRequest() {
  yield takeEvery(ActionTypes.ADD_ALLOWED_PLATE_NUMBER_REQUEST, requestAddAllowedPlateNumber);
}

function* watchDeleteAllowedPlateNumberRequest() {
  yield takeEvery(ActionTypes.DELETE_ALLOWED_PLATE_NUMBER_REQUEST, requestDeleteAllowedPlateNumber);
}

// ==============================
// EXPORT
// ==============================
export default function* ipCamerasSaga() {
  yield all([
    fork(watchIPCamerasListRequest),
    fork(watchIPCameraByIdRequest),
    fork(watchTakeIPCameraPreviewRequest),
    fork(watchFetchIPCameraStateRequest),
    fork(watchSnoozeIPCameraRequest),
    fork(watchWakeUpIPCameraRequest),
    fork(watchFetchIPCameraCapabilitiesRequest),
    fork(watchFetchIPCameraPhotosRequest),
    fork(watchTakeIPCameraPhotoRequest),
    fork(watchTriggerIPCameraRequest),
    fork(watchFetchIPCameraSettingsRequest),
    fork(watchEditIPCameraSettingsRequest),
    fork(watchIPCameraPTZControlRequest),
    fork(watchFetchIPCameraEventsRequest),
    fork(watchEditIPCameraRequest),
    fork(watchRemoveIPCameraRequest),
    fork(watchFetchIPCameraPTZPresetsRequest),
    fork(watchCreateIPCameraPTZPresetRequest),
    fork(watchUpdateIPCameraPTZPresetRequest),
    fork(watchDeleteIPCameraPTZPresetRequest),
    fork(watchActivateIPCameraPTZPresetRequest),
    fork(watchCreateIPCameraRequest),
    fork(watchFetchAllowedPlateNumbersRequest),
    fork(watchAddAllowedPlateNumberRequest),
    fork(watchDeleteAllowedPlateNumberRequest)
  ]);
}
